import { useCallback, useEffect } from "react";
import CommonValidatorProps from "./commonProps";
import useValidator from "./useValidator";
import { useTranslation } from "react-i18next";

type valueType<T> = string | T;

export interface MultiSelectValidatorProps<T>
  extends Omit<CommonValidatorProps<T>, "initialValue" | "validationFunction"> {
  initialValue?: valueType<T>[];
  required?: boolean;
  minLength?: number;
  maxLength?: number;
}

const useMultipleSelectValidator = <T,>(props: MultiSelectValidatorProps<T>) => {
  const { t } = useTranslation();
  const { required, minLength, maxLength } = props;

  const validateRequired = useCallback(
    (value?: valueType<T>[]): [boolean, string] => {
      return [
        required ? value !== undefined && value !== null && value.length > 0 : true,
        t("field:helper.fieldIsRequired"),
      ];
    },
    [required]
  );

  const validateMinLength = useCallback(
    (value?: valueType<T>[]): [boolean, string] => {
      return [
        minLength ? value !== undefined && value.length >= minLength : true,
        `Min number of elements: ${minLength}`,
      ];
    },
    [minLength]
  );

  const validateMaxLength = useCallback(
    (value?: valueType<T>[]): [boolean, string] => {
      return [
        maxLength ? value !== undefined && value.length <= maxLength : true,
        `Max number of elements: ${maxLength}`,
      ];
    },
    [maxLength]
  );

  const validator = useValidator({ ...props, validators: [validateRequired, validateMinLength, validateMaxLength] });

  return validator;
};

export default useMultipleSelectValidator;
